import React, { useState, useEffect, useRef } from 'react';
import { FormControl, InputGroup, ListGroup } from 'react-bootstrap';

const SelectWithSearch = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Cerca o seleziona...",
  label = "",
  getOptionLabel = (option) => option.denominazione || option.label || String(option),
  getOptionValue = (option) => option.id_agenzia || option.id || option.value,
  searchFields = ['denominazione'],
  isLoading = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Aggiorna le opzioni filtrate quando cambiano le opzioni o il termine di ricerca
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = options.filter(option => {
        return searchFields.some(field => {
          const fieldValue = option[field];
          return fieldValue && String(fieldValue).toLowerCase().includes(term);
        });
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, searchFields]);

  // Imposta il valore iniziale del campo di ricerca quando viene selezionato un valore
  useEffect(() => {
    if (value && options.length > 0) {
      const selectedOption = options.find(opt => getOptionValue(opt) === value);
      if (selectedOption) {
        setSearchTerm(getOptionLabel(selectedOption));
      }
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value, options, getOptionLabel, getOptionValue]);

  // Gestisce il click fuori dal componente per chiudere il dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true);
  };

  const handleSelectOption = (option) => {
    const optionValue = getOptionValue(option);
    onChange(optionValue);
    setSearchTerm(getOptionLabel(option));
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange(null);
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const selectedOption = value ? options.find(opt => getOptionValue(opt) === value) : null;
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : searchTerm;

  return (
    <div className="select-with-search-container" ref={containerRef}>
      {label && <span className='small text-muted d-block mb-1'><b>{label}</b></span>}
      <InputGroup className="mb-3">
        <FormControl
          ref={inputRef}
          className="select-with-search-input"
          placeholder={placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          aria-describedby="clear-icon"
        />
        {value && (
          <InputGroup.Text id="clear-icon" onClick={handleClear} role='button' style={{ cursor: 'pointer' }}>
            <i className="bi bi-x-lg"></i>
          </InputGroup.Text>
        )}
        <InputGroup.Text style={{ cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </InputGroup.Text>
      </InputGroup>
      
      {isOpen && !disabled && (
        <div className="select-with-search-dropdown" style={{
          position: 'absolute',
          zIndex: 1000,
          width: '100%',
          maxHeight: '300px',
          overflowY: 'auto',
          backgroundColor: 'white',
          border: '1px solid #ced4da',
          borderRadius: '0.375rem',
          boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
          marginTop: '-0.5rem'
        }}>
          {isLoading ? (
            <div className="text-center p-3">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Caricamento...
            </div>
          ) : filteredOptions.length > 0 ? (
            <ListGroup variant="flush">
              {filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const isSelected = value === optionValue;
                return (
                  <ListGroup.Item
                    key={index}
                    role='button'
                    onClick={() => handleSelectOption(option)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e7f3ff' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {getOptionLabel(option)}
                    {isSelected && <i className="bi bi-check-lg ms-2 text-primary"></i>}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          ) : (
            <div className="text-center text-muted p-3">
              Nessun risultato trovato
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectWithSearch;
